<template>
  <div>
    <div class="el-section">
      <div
        class="emd-container _highlight_header _footer_end _footer_spaced_mobile"
      >
        <div class="emd-container__header">
          <h2 class="emd-text _size_section">{{ $t('webhook.form.title') }}</h2>
        </div>
        <div
          v-if="loadingWebhook || loadingEvents"
          class="eu-pa_6 eu-align_center"
        >
          <emd-loader :type="'_invert'"></emd-loader>
        </div>
        <fieldset
          v-else
          :disabled="fieldsetDisabled"
        >
          <legend></legend>
          <form
            @submit="saveWebhookSettings"
            name="webhookSettings"
            autocomplete="off"
            novalidate="true"
          >
            <div class="emd-container__content">
              <emd-alert
                :variant="formError ? 'danger' : 'success'"
                v-if="showAlert"
                @cancel="showAlert = false"
                :title="
                  formError
                    ? $t('alert.error-title')
                    : $t('alert.success-title')
                "
              >
                <template slot="content">
                  <div v-if="formError">
                    <p
                      v-for="(error, index) in errors"
                      :key="index"
                    >
                      {{ error }}
                    </p>
                  </div>
                  <p v-else>{{ message }}</p>
                </template>
              </emd-alert>
              <div class="emd-panel">
                <div class="emd-panel__section">
                  <div class="emd-panel__block">
                    <div class="el-chunk">
                      <h2 class="emd-text _color_default _type_caption">
                        {{ $t('webhook.form.informations') }}
                      </h2>
                    </div>
                  </div>
                  <div class="emd-panel__block">
                    <div class="el-chunk">
                      <div class="emd-form-group">
                        <label class="emd-label">{{
                          $t('webhook.form.status')
                        }}</label>
                        <label
                          class="emd-switcher__label"
                          for="webhookStatus"
                        >
                          <input
                            class="emd-switcher _color_primary"
                            type="checkbox"
                            id="webhookStatus"
                            v-model="webhookSettings.status"
                          />
                          <div class="emd-switcher__area"></div>
                          <span
                            class="emd-switcher__text"
                            v-if="webhookSettings.status"
                            >{{ $t('webhook.form.active') }}</span
                          >
                        </label>
                      </div>
                    </div>
                    <div class="el-chunk">
                      <div class="emd-form-group">
                        <label class="emd-label _required">{{
                          $t('webhook.form.url')
                        }}</label>
                        <input
                          class="emd-input"
                          type="text"
                          :placeholder="$t('webhook.form.url')"
                          id="url"
                          v-model="webhookSettings.url"
                          v-bind:class="{
                            _state_error:
                              webhookSettings.url &&
                              $v.webhookSettings.url.$invalid
                          }"
                        />
                        <p
                          class="emd-input-feedback _state_error"
                          v-if="
                            webhookSettings.url &&
                            $v.webhookSettings.url.$invalid
                          "
                        >
                          {{ $t('webhook.form.url-error') }}
                        </p>
                        <p
                          class="emd-input-feedback _state_error"
                          v-if="
                            webhookSettings.url &&
                            !$v.webhookSettings.url.maxLength
                          "
                        >
                          {{
                            $t('webhook.form.url-max-length', {
                              maxLength: webhookSettings.maxLengthUrl
                            })
                          }}
                        </p>
                      </div>
                    </div>
                    <div class="el-chunk">
                      <div class="emd-form-group _condensed_30">
                        <label class="emd-label">{{
                          $t('webhook.form.max-retries-label')
                        }}</label>
                        <input
                          class="emd-input"
                          type="text"
                          id="maxAttempts"
                          :max="maxAttempts"
                          :placeholder="
                            $t('webhook.form.max-retries', {
                              maxAttempts: maxAttempts
                            })
                          "
                          v-model="webhookSettings.max_attempts"
                          v-bind:class="{
                            _state_error:
                              $v.webhookSettings.max_attempts.$invalid
                          }"
                        />
                        <p
                          class="emd-input-feedback _state_error"
                          v-if="
                            webhookSettings.max_attempts &&
                            $v.webhookSettings.max_attempts.$invalid
                          "
                        >
                          {{ $t('webhook.form.max-retries-error') }}
                          {{ maxAttempts }}
                          <span v-if="maxAttempts === 1">{{
                            $t('webhook.form.retries-singular')
                          }}</span>
                          <span v-if="maxAttempts > 1">{{
                            $t('webhook.form.retries-plural')
                          }}</span>
                        </p>
                      </div>
                    </div>
                    <div class="el-chunk">
                      <div class="emd-form-group">
                        <label class="emd-label">{{
                          $t('webhook.form.enable-authentication')
                        }}</label>
                        <label
                          class="emd-switcher__label"
                          for="authenticationEnabled"
                        >
                          <input
                            class="emd-switcher _color_primary"
                            type="checkbox"
                            id="authenticationEnabled"
                            v-model="webhookSettings.authentication_enabled"
                          />
                          <div class="emd-switcher__area"></div>
                          <span
                            class="emd-switcher__text"
                            v-if="webhookSettings.authentication_enabled"
                            >{{ $t('webhook.form.active') }}</span
                          >
                        </label>
                      </div>
                    </div>
                    <div
                      class="el-chunk"
                      v-if="webhookSettings.authentication_enabled"
                    >
                      <div class="eu-flex eu-column eu-row_sm">
                        <div class="eu-flex-item eu-basis_6_md">
                          <div class="emd-form-group">
                            <label class="emd-label _required">{{
                              $t('webhook.form.username')
                            }}</label>
                            <input
                              class="emd-input"
                              type="text"
                              id="username"
                              :placeholder="$t('webhook.form.username')"
                              v-model="
                                webhookSettings.basic_authentication.username
                              "
                              v-bind:class="{
                                _state_error:
                                  webhookSettings.basic_authentication
                                    .username &&
                                  $v.webhookSettings.basic_authentication
                                    .username.$invalid
                              }"
                            />
                          </div>
                        </div>
                        <div class="eu-flex-item eu-basis_6_md">
                          <div class="emd-form-group">
                            <label class="emd-label _required">{{
                              $t('webhook.form.password')
                            }}</label>
                            <button
                              v-if="!showInputPassword && webhookSettings.id"
                              v-on:click="
                                openModal('getPasswordAuthentication')
                              "
                              type="button"
                              class="emd-btn _type_link"
                            >
                              {{ $t('webhook.form.click-to-view') }}
                            </button>

                            <div
                              class="emd-input-group"
                              v-if="showInputPassword"
                            >
                              <input
                                class="emd-input fs-hide"
                                :type="inputType"
                                v-model="
                                  webhookSettings.basic_authentication.password
                                "
                                id="password"
                                v-bind:class="{
                                  _state_error:
                                    webhookSettings.basic_authentication
                                      .password &&
                                    $v.webhookSettings.basic_authentication
                                      .password.$invalid
                                }"
                              />
                              <a
                                v-on:click="toggleInput()"
                                class="emd-input-icon__link"
                              >
                                <i
                                  v-if="inputType === 'password'"
                                  class="uil uil-eye"
                                />
                                <i
                                  v-if="inputType === 'text'"
                                  class="uil uil-eye-slash"
                                />
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Events -->
              <div class="emd-panel">
                <div class="emd-panel__section">
                  <div class="emd-panel__block">
                    <div class="el-chunk">
                      <h2 class="emd-text _color_default _type_caption">
                        {{ $t('webhook.form.events') }}
                      </h2>
                    </div>
                  </div>
                  <div class="emd-panel__block">
                    <div class="el-chunk">
                      <div class="el-grid-list _size_large">
                        <div
                          class="emd-container _type_card"
                          v-for="(eventCategorized, index) in eventsCategorized"
                          :key="index"
                        >
                          <div
                            class="emd-checkbox-group"
                            v-bind:class="{
                              _open: eventCategorized.categoryOpenned
                            }"
                          >
                            <div
                              class="emd-checkbox-group__header"
                              v-on:click="
                                toggleCategory(eventCategorized, index)
                              "
                            >
                              <a
                                class="emd-btn-collapse"
                                v-bind:class="{
                                  _open: eventCategorized.categoryOpenned
                                }"
                              >
                                <svg
                                  width="12px"
                                  height="12px"
                                  viewBox="0 0 14 14"
                                  version="1.1"
                                  xmlns="http://www.w3.org/2000/svg"
                                  xmlns:xlink="http://www.w3.org/1999/xlink"
                                >
                                  <path
                                    d="M0,7 L14,7"
                                    id="h"
                                    stroke="#323A43"
                                    stroke-width="2"
                                  />
                                  <path
                                    d="M7,14 L7,0"
                                    id="v"
                                    stroke="#323A43"
                                    stroke-width="2"
                                  />
                                </svg>
                                <span
                                  class="emd-checkbox__text emd-text _size_small _type_label"
                                  >{{ eventCategorized.name }}</span
                                >
                              </a>
                              <jade-checkbox
                                :id="`events-${index}`"
                                :value.sync="eventCategorized.allEventsChecked"
                                :label="$t('webhook.form.select-all')"
                                :indeterminate="
                                  !eventCategorized.allEventsChecked &&
                                  !eventCategorized.allEventsNotChecked &&
                                  eventCategorized.allEventsNotChecked !==
                                    undefined
                                "
                                @click="
                                  toggleCheckAllOfCategory(
                                    eventCategorized,
                                    index
                                  )
                                "
                              />
                            </div>
                            <div class="emd-checkbox-group__content">
                              <jade-checkbox
                                v-for="(event, id) in eventCategorized.events"
                                :key="id"
                                :id="event.name"
                                :label="event.name"
                                :supportText="event.description"
                                :value.sync="event.checked"
                                @click="
                                  toggleEvent(event, eventCategorized, index)
                                "
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="emd-panel"
                v-if="showActions()"
              >
                <div class="emd-panel__section">
                  <div class="emd-panel__block">
                    <div class="el-chunk">
                      <h2 class="emd-text _color_default _type_caption">
                        {{ $t('webhook.form.actions') }}
                      </h2>
                    </div>
                  </div>
                  <div class="emd-panel__block">
                    <ul class="emd-list _space_base _inline eu-flex">
                      <li v-if="webhookSettings.status">
                        <button
                          class="emd-btn _color_secondary"
                          type="button"
                          v-on:click="openModal('disableSettings')"
                        >
                          <i
                            :class="
                              webhookSettings.send_disabled
                                ? 'uil uil-unlock-alt'
                                : 'uil uil-lock-alt'
                            "
                            transform="width-16"
                          />
                          <span v-if="webhookSettings.send_disabled">{{
                            $t('webhook.form.unblock')
                          }}</span>
                          <span v-else>{{ $t('webhook.form.block') }}</span>
                        </button>
                      </li>
                      <li>
                        <button
                          class="emd-btn _color_danger"
                          type="button"
                          v-on:click="openModal('deleteWebhook')"
                        >
                          <i class="uil uil-times-circle" />
                          {{ $t('webhook.form.delete') }}
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <hr class="emd-divider eu-my_4 fade-in" />
              <password-confirmation
                class="fade-in"
                v-model="webhookSettings.password"
                v-show="true"
                :label="labelPassword"
              ></password-confirmation>
            </div>
            <div class="emd-container__footer">
              <button
                class="emd-btn _color_primary _type_text"
                type="button"
                v-on:click="cancelButton"
              >
                {{ $t('webhook.form.cancel') }}
              </button>
              <button
                class="emd-btn _color_primary"
                tabindex="0"
                type="submit"
                :disabled="$v.$invalid"
              >
                <span v-if="!buttonLoading">{{ $t('webhook.form.save') }}</span>
                <emd-loader
                  v-if="buttonLoading"
                  :type="''"
                ></emd-loader>
              </button>
            </div>
          </form>
        </fieldset>
      </div>
    </div>

    <log-links
      :entity="webhookSettings"
      :title="$t('log-links.webhook-settings')"
    ></log-links>

    <modal-password
      v-if="modalOpenned"
      @cancel="closeModal"
      :callback="actionModal"
    ></modal-password>
  </div>
</template>

<style>
.emd-checkbox-group__header {
  cursor: pointer;
}
</style>

<script>
import LogLinks from '@/components/project/molecules/LogLinks/index.vue'
import { EmdLoader, EmdAlert } from 'emerald-vue/src/components'
import { JadeCheckbox } from '@jade'
import {
  required,
  requiredIf,
  url,
  decimal,
  between,
  maxLength
} from 'vuelidate/lib/validators'
import ModalPassword from '@/components/project/globals/ModalPassword.vue'
import PasswordConfirmation from '@/components/project/molecules/PasswordConfirmation.vue'

const MAX_LENGTH_URL_WEBHOOK = 2048

export default {
  name: 'WebhookConfigForm',
  components: {
    EmdLoader,
    EmdAlert,
    ModalPassword,
    PasswordConfirmation,
    LogLinks,
    JadeCheckbox
  },
  data () {
    return {
      loadingWebhook: true,
      loadingEvents: true,
      eventsCategorized: [],
      eventsCheckeds: [],
      categoryOpenned: false,
      webhookSettings: {
        status: true,
        url: null,
        maxLengthUrl: MAX_LENGTH_URL_WEBHOOK,
        max_attempts: null,
        authentication_enabled: false,
        basic_authentication: {
          username: null,
          password: null
        },
        logs_url: {},
        password: ''
      },
      buttonLoading: false,
      modalOpenned: false,
      inputType: 'password',
      showInputPassword: true,
      showAlert: false,
      message: null,
      formError: false,
      errors: [],
      labelPassword: 'webhook.form.confirm-update',
      typeAction: null,
      fieldsetDisabled: false,
      maxAttempts: 3
    }
  },
  validations () {
    return {
      webhookSettings: {
        url: {
          required,
          url,
          maxLength: maxLength(MAX_LENGTH_URL_WEBHOOK)
        },
        max_attempts: {
          between: between(1, this.maxAttempts),
          decimal
        },
        basic_authentication: {
          password: {
            required: requiredIf(() => {
              return (
                this.webhookSettings.authentication_enabled &&
                this.webhookSettings.basic_authentication.password
              )
            })
          },
          username: {
            required: requiredIf(() => {
              return this.webhookSettings.authentication_enabled
            })
          }
        },
        password: {
          required
        }
      }
    }
  },
  computed: {
    merchant () {
      return this.$store.getters.merchant
    },
    account () {
      return this.$store.getters.account
    },
    isTestAccount () {
      return this.account.account_type.value === 'test'
    },
    lastLog () {
      return this.$store.getters.lastLog
    },
    params () {
      return {
        merchantId: this.merchant.id,
        accountId: this.account.id,
        webhookId: this.$route.params.id
      }
    }
  },
  methods: {
    getWebhook () {
      this.$store
        .dispatch('GET_WEBHOOK_SETTINGS_BY_ID', this.params)
        .then(res => {
          this.loadingWebhook = false
          this.prepareForEdition(res)
          this.getLastLog(res.id)
        })
    },
    getLastLog (entityId) {
      const params = {
        merchantId: this.merchant.id,
        accountId: this.account.id,
        entityId: entityId
      }
      this.$store.dispatch('GET_LAST_LOG', params)
    },
    prepareForEdition (webhook) {
      const events = webhook.events
      this.eventsCategorized.map((category, index) => {
        category.events.map(function (evts) {
          const evt = events.filter(function (e) {
            return e === evts.name
          })
          if (evt.length && evt[0] === evts.name) {
            evts.checked = true
          }
          return evts
        })
        this.verifyCheckedCategory(category, index)
      })
      this.webhookSettings.id = webhook.id
      this.webhookSettings.url = webhook.url
      this.webhookSettings.max_attempts = webhook.max_attempts
      this.webhookSettings.authentication_enabled =
        webhook.authentication_type !== 'none'
      this.webhookSettings.basic_authentication =
        webhook.basic_authentication || {}
      this.webhookSettings.status = webhook.status === 'active'
      this.webhookSettings.send_disabled = webhook.send_disabled
      this.webhookSettings.logs_url = webhook.logs_url

      this.showInputPassword = webhook.authentication_type === 'none'
    },
    verifyCheckedCategory (category, index) {
      const checkedEvents =
        category.events.filter(ev => ev.checked === true) || []
      const allEventsChecked = checkedEvents.length === category.events.length
      const allEventsNotChecked = checkedEvents.length === 0
      this.$set(category, 'allEventsChecked', allEventsChecked)
      this.$set(category, 'allEventsNotChecked', allEventsNotChecked)
      this.refreshEventsCheckeds()
      if (checkedEvents.length && category.categoryOpenned !== true) {
        this.toggleCategory(category, index)
      }
    },
    toggleCheckAllOfCategory (category, index) {
      let boolean = true
      if (category.allEventsChecked) {
        boolean = false
      }
      category.events.forEach(function (event) {
        event.checked = boolean
      })
      this.$set(category, 'allEventsChecked', boolean)
      this.$set(category, 'allEventsNotChecked', !boolean)
      this.refreshEventsCheckeds()
      this.$set(this.eventsCategorized[index], 'categoryOpenned', !boolean)
    },
    toggleEvent (event, category, index) {
      event.checked = !event.checked
      this.verifyCheckedCategory(category, index)
    },
    toggleCategory (category, index) {
      const openned = !category.categoryOpenned
      this.$set(this.eventsCategorized[index], 'categoryOpenned', openned)
    },
    refreshEventsCheckeds () {
      this.eventsCheckeds = []
      this.eventsCategorized.map(category => {
        category.events.map(evt => {
          if (evt.checked) {
            this.eventsCheckeds.push(evt.name)
          }
        })
      })
    },
    getAuthenticationType () {
      let authenticationType = 'none'
      if (this.webhookSettings.authentication_enabled) {
        if (this.merchant.id === 'merch_7c1d4fd5addb4b6') {
          authenticationType = this.isTestAccount ? 'avon_test' : 'avon'
        } else {
          authenticationType = 'basic'
        }
      }
      return authenticationType
    },
    saveWebhookSettings (e) {
      e.preventDefault()
      const authenticationType = this.getAuthenticationType()
      const status = this.webhookSettings.status ? 'active' : 'inactive'
      this.fieldsetDisabled = true
      const body = {
        url: this.webhookSettings.url,
        status: status,
        events: this.eventsCheckeds,
        interval: 300,
        max_attempts: this.webhookSettings.max_attempts
          ? this.webhookSettings.max_attempts
          : this.isTestAccount
          ? 1
          : 3,
        authentication_type: authenticationType,
        password: window.btoa(this.webhookSettings.password)
      }
      if (authenticationType !== 'none') {
        body.basic_authentication = {
          username: this.webhookSettings.basic_authentication
            ? this.webhookSettings.basic_authentication.username
            : '',
          password: this.webhookSettings.basic_authentication
            ? this.webhookSettings.basic_authentication.password
            : ''
        }
      }
      if (this.$route.params.id) {
        this.updateWebhookSettings(body)
      } else {
        this.createWebhookSettings(body)
      }
    },
    updateWebhookSettings (body) {
      this.params.body = body
      this.errors = []
      this.buttonLoading = true
      const msgErrorInternal = this.$t('webhook.form.error-internal')
      this.$store
        .dispatch('UPDATE_WEBHOOK_SETTINGS', this.params)
        .then(res => {
          window.scrollTo(0, 0)
          this.webhookSettings.password = ''
          this.buttonLoading = false
          this.message = this.$t('webhook.form.success-updated-message')
          this.formError = false
          this.showAlert = true
          this.fieldsetDisabled = false
          this.getWebhook()
        })
        .catch(err => {
          window.scrollTo(0, 0)
          this.formError = true
          this.showAlert = true
          this.fieldsetDisabled = false
          this.buttonLoading = false
          if (err?.body) {
            Object.values(err.body).forEach(errors => {
              const [error] = errors
              if (!this.errors.includes(error)) {
                this.errors.push(error)
              }
            })
          } else {
            this.errors.push(msgErrorInternal)
          }
        })
    },
    createWebhookSettings (body) {
      this.params.body = body
      this.errors = []
      this.buttonLoading = true
      const msgErrorInternal = this.$t('webhook.form.error-internal')
      this.$store
        .dispatch('CREATE_WEBHOOK_SETTINGS', this.params)
        .then(res => {
          window.scrollTo(0, 0)
          this.webhookSettings.password = ''
          this.buttonLoading = false
          this.message = this.$t('webhook.form.success-created-message')
          this.formError = false
          this.showAlert = true
          this.fieldsetDisabled = false
          this.$router
            .push({ name: 'webhook-config-edit', params: { id: res.id } })
            .catch(err => err)
          this.getWebhook()
        })
        .catch(err => {
          window.scrollTo(0, 0)
          this.formError = true
          this.showAlert = true
          this.buttonLoading = false
          this.fieldsetDisabled = false
          if (err?.body) {
            Object.values(err.body).forEach(errors => {
              const [error] = errors
              if (!this.errors.includes(error)) {
                this.errors.push(error)
              }
            })
          } else {
            this.errors.push(msgErrorInternal)
          }
        })
    },
    showActions () {
      return this.$route.params.id
    },
    openModal (type) {
      this.modalOpenned = true
      this.typeAction = type
    },
    closeModal () {
      this.modalOpenned = false
      this.fieldsetDisabled = false
      this.buttonLoading = false
    },
    toggleInput () {
      this.inputType = this.inputType === 'text' ? 'password' : 'text'
    },
    cancelButton () {
      this.$router.push({ name: 'webhook-config' }).catch(err => err)
    },
    actionModal (password) {
      this.fieldsetDisabled = true
      return this[this.typeAction](password)
    },
    getPasswordAuthentication (password) {
      this.params.body = { password: password }
      return this.$store
        .dispatch('GET_PASSWORD_AUTHENTICATION', this.params)
        .then(res => {
          this.$set(
            this.webhookSettings.basic_authentication,
            'password',
            res.password
          )
          this.inputType = 'text'
          this.showInputPassword = true
          this.fieldsetDisabled = false
        })
    },
    deleteWebhook (password) {
      this.errors = []
      this.params.body = { password: password }
      const msgErrorInternal = this.$t('webhook.form.error-internal')
      return this.$store
        .dispatch('DELETE_WEBHOOK_SETTINGS', this.params)
        .then(res => {
          this.formError = false
          this.showAlert = true
          this.fieldsetDisabled = true
          this.$router
            .push({
              name: 'webhook-config',
              params: {
                merchant_id: this.merchant.id,
                account_id: this.account.id
              }
            })
            .catch(err => err)
        })
        .catch(err => {
          window.scrollTo(0, 0)
          this.formError = true
          this.showAlert = true
          this.fieldsetDisabled = false
          if (err?.body) {
            Object.values(err.body).forEach(errors => {
              const [error] = errors
              if (!this.errors.includes(error)) {
                this.errors.push(error)
              }
            })
          } else {
            this.errors.push(msgErrorInternal)
          }
        })
    },
    disableSettings (password) {
      this.errors = []
      this.params.body = {
        send_disabled: !this.webhookSettings.send_disabled,
        password: password
      }
      const msgErrorInternal = this.$t('webhook.form.error-internal')
      return this.$store
        .dispatch('SEND_DISABLED_WEBHOOK', this.params)
        .then(res => {
          window.scrollTo(0, 0)
          this.message = this.$t('webhook.form.success-updated-message')
          this.formError = false
          this.showAlert = true
          this.fieldsetDisabled = false
          this.getWebhook()
        })
        .catch(err => {
          window.scrollTo(0, 0)
          this.formError = true
          this.showAlert = true
          this.fieldsetDisabled = false
          if (err.body && Array.isArray(err.body.errors)) {
            err.body.errors.forEach(error => {
              if (!this.errors.includes(error.message)) {
                this.errors.push(error.message)
              }
            })
          } else {
            this.errors.push(msgErrorInternal)
          }
        })
    },
    setFirstOpened () {
      if (this.eventsCategorized.length) {
        this.$set(this.eventsCategorized[0], 'categoryOpenned', true)
      }
    }
  },
  created () {
    this.$store.dispatch('GET_EVENTS_CATEGORIZED').then(res => {
      this.eventsCategorized = res
      this.loadingEvents = false
      if (this.$route.params.id) {
        this.labelPassword = 'webhook.form.confirm-change'
        this.getWebhook()
      } else {
        this.labelPassword = 'webhook.form.confirm-creation'
        this.loadingWebhook = false
        this.setFirstOpened()
      }
    })
  }
}
</script>
